import React from 'react';
import { Type, Image as ImageIcon, Youtube, MousePointerClick, FileText, Columns2, Minus } from 'lucide-react';
import type { Block } from '@staty/shared/schemas/block';
import { newBlock } from '@staty/shared/utils/blockUtils';

/**
 * The "+" palette: one entry per block type. `allowColumns={false}` inside
 * column cells — the schema is depth-1 (columns cannot contain columns).
 *
 * "Document" is not a block type: it opens a PDF picker (handled by the
 * inserter) and lands as a Button block pointing at the uploaded file.
 */

interface PaletteEntry {
  type: Block['type'];
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const ENTRIES: PaletteEntry[] = [
  { type: 'richtext', label: 'Text', icon: Type },
  { type: 'image', label: 'Image', icon: ImageIcon },
  { type: 'embed', label: 'Video', icon: Youtube },
  { type: 'button', label: 'Button', icon: MousePointerClick },
  { type: 'columns', label: 'Columns', icon: Columns2 },
  { type: 'divider', label: 'Divider', icon: Minus },
];

interface Props {
  allowColumns: boolean;
  onPick: (block: Block) => void;
  onPickDocument?: () => void;
}

export default function BlockPalette({ allowColumns, onPick, onPickDocument }: Props) {
  return (
    <div className="canvas-palette" role="menu">
      {ENTRIES.filter((e) => allowColumns || e.type !== 'columns').map((e) => (
        <React.Fragment key={e.type}>
          <button
            type="button"
            role="menuitem"
            className="canvas-palette__item"
            onClick={() => onPick(newBlock(e.type))}
          >
            <e.icon className="h-4 w-4" />
            <span>{e.label}</span>
          </button>
          {e.type === 'button' && onPickDocument && (
            <button
              type="button"
              role="menuitem"
              className="canvas-palette__item"
              onClick={onPickDocument}
            >
              <FileText className="h-4 w-4" />
              <span>Document</span>
            </button>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
